import { NOTIFICATIONS } from '@src/graphql/queries/notification';
import { useMemo } from 'react';
import { usePaginatedFetchItems } from 'pe-core-frontend';
/**
 * Custom hook to fetch and manage notifications for a specific client and user.
 *
 * @param {UseNotificationsProps} props - The properties required to fetch notifications.
 * @param {string} props.clientId - The ID of the client.
 * @param {string} props.recipientId - The ID of the user.
 *
 * @returns {object} An object containing the notifications data and related state.
 * @returns {Notification[]} returns.notifications - The list of notifications.
 * @returns {Error | null} returns.error - The error object if an error occurred, otherwise null.
 * @returns {boolean} returns.loading - The loading state of the notifications fetch.
 * @returns {number} returns.page - The current page number.
 * @returns {function} returns.setPage - Function to set the current page number.
 * @returns {number} returns.pageSize - The number of notifications per page.
 * @returns {function} returns.setPageSize - Function to set the number of notifications per page.
 * @returns {number} returns.total - The total number of notifications.
 */
export const useNotifications = ({ clientId, recipientId }) => {
    const spec = useMemo(() => ({
        clientId,
        recipientId,
    }), [clientId, recipientId]);
    const { items: notifications, error, loading, page, setPage, pageSize, setPageSize, total, numPages, } = usePaginatedFetchItems({
        fetchQuery: NOTIFICATIONS,
        dataProp: 'notifications',
        spec,
        initialPageSize: 10,
    });
    return {
        notifications,
        error,
        loading,
        page,
        setPage,
        pageSize,
        setPageSize,
        total,
        numPages,
    };
};
